import { PlayIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { Video } from './Video';

interface ThumbnailProps {
  src?: string;
  shouldShowDuration?: boolean;
}

/**
 * VideoThumbnail component displays a video thumbnail with an optional duration overlay.
 * If no source is provided, it shows a placeholder with a play icon.
 *
 * - **src** [string - optional] - The source URL of the video thumbnail
 * - **shouldShowDuration** [boolean - optional] - Whether to show the video duration overlay.
 */
const VideoThumbnail = (props: ThumbnailProps) => {
  const { src, shouldShowDuration } = props;

  const [duration, setDuration] = useState<number | null>(null);

  const handleMetadataLoaded = (
    event: React.SyntheticEvent<HTMLVideoElement>,
  ) => {
    const videoElement = event.currentTarget;
    if (videoElement.duration) {
      setDuration(videoElement.duration);
    }
  };

  const formatDuration = (): string | undefined => {
    if (duration === null) {
      return undefined;
    }

    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  // If no src is provided show a placeholder (black square with play icon)
  if (!src) {
    return (
      <div className="flex size-full items-center justify-center bg-surface-200">
        <PlayIcon className="size-4 text-surface-100 drop-shadow-icon" />
      </div>
    );
  }

  return (
    <div className="relative flex size-full items-center justify-center bg-surface">
      <Video
        url={src}
        controls={false}
        autoPlay={false}
        videoProps={{
          onLoadedMetadata: handleMetadataLoaded,
        }}
      />
      {shouldShowDuration ? (
        <div className="absolute bottom-0 right-0 flex rounded-tl-lg bg-dark/70 px-2 py-1 text-xs text-white">
          <PlayIcon className="size-4 text-white drop-shadow-icon" />
          {duration && <span className="ml-1">{formatDuration()}</span>}
        </div>
      ) : (
        <div className="absolute flex items-center justify-center">
          <PlayIcon className="size-4 text-white drop-shadow-icon" />
        </div>
      )}
    </div>
  );
};

export default VideoThumbnail;
